import { type Context, createContext, useContext } from 'react';
import { type BlobInput, type PageSize } from '@oicana/browser';

export interface DocumentCompileRequest {
    template: string;
    jsonDatasets: Map<string, string>;
    blobDatasets: Map<string, BlobInput>;
}

export interface CompiledDocumentPreview {
    documentToken: number;
    pages: PageSize[];
}

export interface RenderPageRequest {
    documentToken: number;
    pageIndex: number;
    pixelsPerPt: number;
}

export interface RenderResult {
    result: Blob;
}

export enum WorkerState {
    Initializing,
    Ready,
    Broken,
}

interface DocumentsContext {
    state: WorkerState;
    compile: (request: DocumentCompileRequest) => Promise<CompiledDocumentPreview | undefined>;
    renderPage: (request: RenderPageRequest) => Promise<RenderResult | undefined>;
    release: (documentToken: number) => void;
}

export const useDocumentsWorker = () => {
    const { state, compile, renderPage, release } = useContext(DocumentsContext);

    return {
        state,
        compile,
        renderPage,
        release,
    };
};

const defaultDocumentsContext: DocumentsContext = {
    state: WorkerState.Initializing,
    compile: () => Promise.resolve(undefined),
    renderPage: () => Promise.resolve(undefined),
    release: () => {},
};

export const DocumentsContext: Context<DocumentsContext> = createContext(defaultDocumentsContext);
